import React, { useState, useEffect } from 'react';
import { adminApiClient, DonationCause } from '../lib/api';
import { getAssetUrl } from '../lib/utils';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import { DataTable } from './common/DataTable';
import {
    Image as ImageIcon,
    Upload,
    Pencil,
    Trash2,
    X,
    Plus,
    Search
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';

const DonationCausesPage: React.FC = () => {
    const [causes, setCauses] = useState<DonationCause[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingCause, setEditingCause] = useState<DonationCause | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        suggestedAmount: undefined as number | undefined,
        isCustomAmount: true,
        minAmount: 1,
        isActive: true,
        isPopular: false,
        order: 0,
        image: ''
    });

    const fetchCauses = async () => {
        try {
            setLoading(true);
            const response = await adminApiClient.getDonationCauses();

            if (response.success && response.data) {
                const data = Array.isArray(response.data) ? response.data : (response.data as any).data;
                setCauses(data || []);
            } else {
                toast.error(response.message || 'Failed to fetch donation causes');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error fetching donation causes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCauses();
    }, []);

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            suggestedAmount: undefined,
            isCustomAmount: true,
            minAmount: 1,
            isActive: true,
            isPopular: false,
            order: 0,
            image: ''
        });
        setEditingCause(null);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setUploading(true);
            const response = await adminApiClient.uploadImage(file);
            if (response.success && response.data) {
                const imagePath = response.data.path || response.data.fullPath || response.data.url || '';
                setFormData({ ...formData, image: imagePath });
            } else {
                toast.error(response.message || 'Failed to upload image');
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            toast.error(err.message || 'Error uploading image');
        } finally {
            setUploading(false);
            // Reset input value to allow re-uploading same file if needed
            e.target.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const toastId = toast.loading('Saving...');
        try {
            const submitData = { ...formData, icon: 'wallet' };
            let response;
            if (editingCause) {
                response = await adminApiClient.updateDonationCause(editingCause._id, submitData);
            } else {
                response = await adminApiClient.createDonationCause(submitData);
            }

            if (response.success) {
                toast.success('Saved successfully', { id: toastId });
                fetchCauses();
                setShowForm(false);
                resetForm();
            } else {
                toast.error(response.message || 'Failed to save', { id: toastId });
            }
        } catch (err: any) {
            toast.error(err.message || 'Error saving', { id: toastId });
        }
    };

    const handleEdit = (cause: DonationCause) => {
        setEditingCause(cause);
        setFormData({
            title: cause.title,
            description: cause.description,
            suggestedAmount: cause.suggestedAmount,
            isCustomAmount: cause.isCustomAmount,
            minAmount: cause.minAmount || 1,
            isActive: cause.isActive,
            isPopular: cause.isPopular,
            order: cause.order,
            image: cause.image || ''
        });
        setShowForm(true);
    };

    const handleDelete = async (id: string) => {
        const isConfirmed = await showDeleteConfirmation('Delete Cause?', 'This usage cannot be undone.');
        if (!isConfirmed) return;

        try {
            const response = await adminApiClient.deleteDonationCause(id);
            if (response.success) {
                toast.success('Deleted successfully');
                fetchCauses();
            } else {
                toast.error(response.message || 'Failed to delete');
            }
        } catch (err: any) {
            toast.error(err.message);
        }
    };

    const columns: ColumnDef<DonationCause>[] = [
        {
            accessorKey: 'image',
            header: 'Image',
            cell: (info) => (
                <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden border border-gray-200">
                    {info.getValue<string>() ? (
                        <img src={getAssetUrl(info.getValue<string>())} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <div className="flex items-center justify-center w-full h-full text-gray-400">
                            <ImageIcon size={20} />
                        </div>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: (info) => (
                <div>
                    <div className="font-medium text-gray-900">{info.getValue<string>()}</div>
                    <div className="text-xs text-gray-500 line-clamp-1 max-w-xs">{info.row.original.description}</div>
                </div>
            )
        },
        {
            header: 'Pricing',
            accessorKey: 'minAmount',
            cell: (info) => (
                <div className="text-sm">
                    <div className="flex gap-1 text-gray-600"><span className="text-xs text-gray-400 w-8">Min:</span> ${info.getValue<number>()}</div>
                    {info.row.original.suggestedAmount && (
                        <div className="flex gap-1 text-gray-600"><span className="text-xs text-gray-400 w-8">Sugg:</span> ${info.row.original.suggestedAmount}</div>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'isActive',
            header: 'Status',
            cell: (info) => (
                <div className="flex flex-col gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium w-fit ${info.getValue<boolean>() ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {info.getValue<boolean>() ? 'Active' : 'Inactive'}
                    </span>
                    {info.row.original.isPopular && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[color-mix(in_srgb,var(--color-primary)_12%,white)] text-[color-mix(in_srgb,var(--color-primary)_80%,black)] w-fit">Popular</span>
                    )}
                </div>
            )
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (info) => (
                <div className="flex gap-2">
                    <button onClick={() => handleEdit(info.row.original)} className="p-1 text-[var(--color-secondary)] hover:bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] rounded" title="Edit">
                        <Pencil size={18} />
                    </button>
                    <button onClick={() => handleDelete(info.row.original._id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Remove">
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    const filteredCauses = causes.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Donation Causes</h1>
                    <p className="text-sm text-gray-500">Manage donation types and funds</p>
                </div>
                <button
                    onClick={() => { resetForm(); setShowForm(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition-colors shadow-sm"
                >
                    <Plus size={20} /> Add Cause
                </button>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-gray-900">{editingCause ? 'Edit Cause' : 'New Cause'}</h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 h-[80vh] overflow-y-auto">
                            {/* Image Upload */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-20 h-20 rounded-lg bg-gray-100 border flex items-center justify-center overflow-hidden">
                                        {formData.image ? <img src={getAssetUrl(formData.image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="text-gray-400" />}
                                    </div>
                                    <div>
                                        <label className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none cursor-pointer">
                                            <Upload className="-ml-1 mr-2 h-4 w-4" />
                                            {uploading ? 'Uploading...' : 'Change'}
                                            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input type="text" required value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" placeholder="e.g. Annadanam" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea required rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" placeholder="Description..."></textarea>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                                            <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} min="1" value={formData.minAmount} onChange={e => setFormData({ ...formData, minAmount: parseFloat(e.target.value) || 1 })} className="w-full pl-6 pr-3 py-2 border rounded-lg outline-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Suggested</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                                            <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} min="0" value={formData.suggestedAmount || ''} onChange={e => setFormData({ ...formData, suggestedAmount: parseFloat(e.target.value) || undefined })} className="w-full pl-6 pr-3 py-2 border rounded-lg outline-none" placeholder="Optional" />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2 pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.isCustomAmount} onChange={e => setFormData({ ...formData, isCustomAmount: e.target.checked })} className="rounded text-[var(--color-secondary)] focus:ring-[var(--color-secondary)]" />
                                        <span className="text-sm text-gray-700">Allow Custom Amount</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} className="rounded text-green-600 focus:ring-green-500" />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={formData.isPopular} onChange={e => setFormData({ ...formData, isPopular: e.target.checked })} className="rounded text-[var(--color-primary)] focus:ring-[var(--color-primary)]" />
                                        <span className="text-sm text-gray-700">Mark as Popular</span>
                                    </label>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                                    <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} value={formData.order} onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                                </div>
                            </div>

                            <div className="pt-6 flex justify-end gap-3 sticky bottom-0 bg-white border-t mt-6">
                                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                                <button type="submit" className="px-4 py-2 text-white bg-[var(--color-primary)] rounded-lg hover:opacity-90">Save Cause</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search causes..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none"
                    />
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTable
                    data={filteredCauses}
                    columns={columns}
                    loading={loading}
                    enableClientSidePagination={true}
                />
            </div>
        </div>
    );
};

export default DonationCausesPage;
